import { useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Card, Stack, Group, Button, Text, Title } from '@repletoryreach/sdk-ui';
import { usePaletteStore } from '@repletoryreach/sdk-utils/store';
import { ADMIN_NAV } from '../../lib/nav';
import { navIcon } from '../../app/shell/nav-icons';
import { formatDateTime } from '../../lib/format';
import { useT, type Translate } from '../../i18n/I18nProvider';
import './not-found.css';

const MAX_SUGGESTIONS = 3;

/**
 * The nav entries closest to the path that missed.
 *
 * Scoring is deliberately crude: how many leading path segments the entry
 * shares with what was asked for, then how much of the entry's own label the
 * typed text covers. A stale runbook link to /integrations should land on
 * Settings → Integrations, and that is the whole ambition here.
 */
function suggest(pathname: string, t: Translate): Array<{ path: string; label: string }> {
  const wanted = pathname.toLowerCase().split('/').filter(Boolean);
  if (wanted.length === 0) return [];
  const scored = ADMIN_NAV.flatMap((section) => section.items).map((item) => {
    const segments = item.path.toLowerCase().split('/').filter(Boolean);
    let score = 0;
    for (const segment of segments) {
      if (wanted.includes(segment)) score += 2;
      else if (wanted.some((w) => segment.startsWith(w) || w.startsWith(segment))) score += 1;
    }
    return { path: item.path, label: t(item.labelKey), score };
  });
  return scored
    .filter((s) => s.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, MAX_SUGGESTIONS);
}

/** A signpost with nothing to point at. Decorative, hence `aria-hidden`. */
function SignpostArt() {
  return (
    <svg className="admin-404__art" viewBox="0 0 240 190" fill="none" aria-hidden focusable="false">
      <g stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" opacity="0.5">
        <path d="M118 24v150" />
        <path d="M84 172h68" />
        <path d="M118 52h56l16 15-16 15h-56z" />
        <path d="M118 96H52l-16 15 16 15h66z" />
        <path d="M22 46c6-5 14-5 20 0M196 150c6-5 14-5 20 0" />
      </g>
      <circle cx="118" cy="34" r="17" fill="var(--rr-color-primary)" />
      <path d="M112 28l12 12M124 28l-12 12" stroke="var(--rr-color-surface)" strokeWidth="2.6" strokeLinecap="round" />
    </svg>
  );
}

export default function NotFoundPage() {
  const t = useT();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const openPalette = usePaletteStore((s) => s.openPalette);

  // This page is a live destination, not only a typo catcher: at least one
  // in-app link points at a route that does not exist, so naming the path is
  // what turns a dead end into a bug report.
  const suggestions = useMemo(() => suggest(pathname, t), [pathname, t]);

  // The moment the miss happened, so an operator reporting a stale link can
  // say when. The reference design also carries an error ID; nothing here
  // generates or records one, and a random string nobody can look up would
  // only waste the reporter's time.
  const occurredAt = useMemo(() => new Date().toISOString(), []);

  return (
    <Stack gap="lg">
      <Card withBorder>
        <Stack gap="lg">
          <div className="admin-404">
            <SignpostArt />
            <Stack gap="md" align="flex-start">
              <h1 className="admin-404__title">{t('notFound.title')}</h1>
              <Text c="var(--rr-color-text-muted)">{t('notFound.description')}</Text>
              <code className="admin-404__path">{pathname}</code>
              <Group gap="sm" wrap="wrap">
                <Button onClick={() => navigate('/')}>{t('notFound.dashboardAction')}</Button>
                <Button variant="default" onClick={() => openPalette()}>{t('notFound.openPalette')}</Button>
                <button type="button" className="admin-404__help-link" onClick={() => navigate(-1)}>
                  {t('notFound.goBack')}
                </button>
              </Group>
            </Stack>
          </div>

          {suggestions.length > 0 && (
            <Stack gap="sm">
              <Title order={4}>{t('notFound.suggestionsTitle')}</Title>
              <Text fz="sm" c="var(--rr-color-text-muted)">{t('notFound.suggestionsHint')}</Text>
              <div className="admin-404__suggestions">
                {suggestions.map((s) => (
                  <Link key={s.path} to={s.path} className="admin-404__suggestion">
                    <span className="admin-404__suggestion-icon">{navIcon(s.path)}</span>
                    <span className="admin-404__suggestion-body">
                      <Text fz="sm" fw={600}>{s.label}</Text>
                      {/* The route, not a description: the nav model carries no
                          copy for these entries, and inventing a sentence per
                          page is how a console starts lying about itself. */}
                      <Text fz="xs" c="var(--rr-color-primary)">{s.path}</Text>
                    </span>
                    <span className="admin-404__chevron" aria-hidden>→</span>
                  </Link>
                ))}
              </div>
            </Stack>
          )}

          <div className="admin-404__help">
            <div className="admin-404__help-item">
              <Stack gap="xs">
                <Text fz="sm" fw={600}>{t('notFound.help.title')}</Text>
                <Text fz="xs" c="var(--rr-color-text-muted)">{t('notFound.help.body')}</Text>
              </Stack>
            </div>
            <div className="admin-404__help-item">
              <Stack gap="xs">
                <Text fz="sm" fw={600}>{t('notFound.help.searchTitle')}</Text>
                <Text fz="xs" c="var(--rr-color-text-muted)">{t('notFound.help.searchBody')}</Text>
                <button type="button" className="admin-404__help-link" onClick={() => openPalette()}>
                  {t('notFound.openPalette')} <span aria-hidden>→</span>
                </button>
              </Stack>
            </div>
            <div className="admin-404__help-item">
              <Stack gap="xs">
                <Text fz="sm" fw={600}>{t('notFound.help.statusTitle')}</Text>
                <Text fz="xs" c="var(--rr-color-text-muted)">{t('notFound.help.statusBody')}</Text>
                <Link to="/system/health" className="admin-404__help-link">
                  {t('notFound.help.statusAction')} <span aria-hidden>→</span>
                </Link>
              </Stack>
            </div>
          </div>

          <Group justify="flex-end">
            <Text fz="xs" c="var(--rr-color-text-muted)" title={occurredAt}>
              {t('notFound.occurred', { when: formatDateTime(occurredAt) })}
            </Text>
          </Group>
        </Stack>
      </Card>
    </Stack>
  );
}
